/* ═════════════════════════════════════════════════════════════════
   Admin · Vinci Bureaucratic Library
   ═════════════════════════════════════════════════════════════════
   Read-only gallery of the 25 "red-tape" template PDFs, grouped by the
   five compliance sectors. Each card opens the Legal-Grey preview in a
   new tab. Correction Pool lives at /admin/corrections.
   ═════════════════════════════════════════════════════════════════ */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Scale, FileText, ExternalLink, Download, ShieldCheck, ArrowLeft, Send, History, LayoutGrid,
} from "lucide-react";
import { BureaucraticShareDialog } from "@/components/BureaucraticShareDialog";
import { BureaucraticSharedHistory } from "@/components/BureaucraticSharedHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBureaucraticLibrary() {
  const [sectors, setSectors] = useState([]);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("library");
  const [shareTpl, setShareTpl] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bureaucratic/templates`);
        if (alive) setSectors(data.sectors || []);
      } catch { toast.error("Unable to load the Bureaucratic Library"); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F2F0EA" }}>
      {/* Header band */}
      <div className="border-b" style={{ background: "#1F2226", borderColor: "#4A5058" }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="bureaucratic-back-admin"
                    className="flex items-center gap-1.5 text-[10px] font-grotesk uppercase tracking-[0.28em] text-[#C4C8CC] hover:text-white">
              <ArrowLeft size={12} /> Admin
            </Link>
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#7A8590] font-mono">
                Vinci Executive Command · Bureaucratic
              </div>
              <h1 className="font-serif text-2xl text-[#E4E6E8] tracking-wide mt-1">
                The Bureaucratic Library
              </h1>
              <p className="text-[11px] text-[#8A9098] mt-1 max-w-2xl font-mono">
                Twenty-five compliance instruments across five sectors — each a
                monumental PDF filed under the Vinci "Red-Tape" Doctrine.
              </p>
            </div>
          </div>
          <Link to="/admin/corrections"
                data-testid="bureaucratic-go-correction-pool"
                className="flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.28em] border border-[#C4C8CC]/40 text-[#E4E6E8] hover:bg-[#4A5058] px-4 py-2 rounded-sm">
            <ShieldCheck size={12} /> Correction Pool →
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-8 border-b" style={{ borderColor: "#C4C8CC" }}>
          <TabButton active={tab === "library"} onClick={() => setTab("library")} icon={<LayoutGrid size={12} />} label="Library" testid="tab-library" />
          <TabButton active={tab === "history"} onClick={() => setTab("history")} icon={<History size={12} />} label="Shared History" testid="tab-history" />
        </div>
        {tab === "history" ? (
          <BureaucraticSharedHistory refreshKey={historyKey} />
        ) : busy ? (
          <div className="text-center py-24 text-[#6B6F76] font-mono text-sm">
            Loading the ledger…
          </div>
        ) : sectors.map((s) => <SectorBlock key={s.id} sector={s} onShare={setShareTpl} />)}
      </div>

      {shareTpl && (
        <BureaucraticShareDialog
          template={shareTpl}
          onClose={() => setShareTpl(null)}
          onShared={() => setHistoryKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={`bureaucratic-${testid}`}
            className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] px-4 py-3 -mb-px border-b-2 transition-colors ${
              active ? "border-[#1F2226] text-[#161A1E]" : "border-transparent text-[#7A8590] hover:text-[#2C3138]"}`}>
      {icon} {label}
    </button>
  );
}

function SectorBlock({ sector, onShare }) {
  return (
    <section className="mb-12" data-testid={`bureaucratic-sector-${sector.id}`}>
      <div className="flex items-baseline gap-4 mb-5 border-b border-[#4A5058]/25 pb-3">
        <span className="font-serif text-4xl text-[#2C3138]">{sector.roman}</span>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A8590]">
            Sector · <span className="text-[#4A5058]">{sector.kicker}</span>
          </div>
          <h2 className="font-serif text-xl text-[#161A1E]">{sector.name}</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {sector.templates.map((t) => <TemplateCard key={t.slug} t={t} onShare={onShare} />)}
      </div>
    </section>
  );
}

function TemplateCard({ t, onShare }) {
  const url = `${API}/bureaucratic/templates/${t.slug}/preview`;
  return (
    <div
      data-testid={`bureaucratic-template-${t.slug}`}
      className="relative border p-4 flex flex-col justify-between min-h-[190px] transition-shadow hover:shadow-lg"
      style={{
        background: "linear-gradient(180deg, #E4E6E8 0%, #F2F0EA 100%)",
        borderColor: "#7A8590",
        boxShadow: "inset 0 0 0 1px rgba(196,200,204,0.7)",
      }}
    >
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <FileText size={12} className="text-[#4A5058]" />
          <span className="text-[9px] font-mono uppercase tracking-[0.24em] text-[#6B6F76]">
            Template
          </span>
        </div>
        <h3 className="font-serif text-[15px] text-[#161A1E] leading-snug">
          {t.title}
        </h3>
        <p className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#6B6F76]">
          {t.purpose}
        </p>
      </div>
      <div className="mt-3 pt-3 border-t border-[#7A8590]/40 space-y-2.5">
        <button onClick={() => onShare(t)}
                data-testid={`bureaucratic-share-${t.slug}`}
                className="w-full flex items-center justify-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.24em] bg-[#1F2226] text-[#E4E6E8] hover:bg-[#2C3138] hover:text-[#D8B23E] py-2 rounded-sm transition-colors">
          <Send size={10} /> Share to Customer
        </button>
        <div className="flex items-center justify-between">
        {t.correction_form ? (
          <Link to={`/corrections/submit/${t.correction_form}`}
                data-testid={`bureaucratic-file-${t.slug}`}
                className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.22em] text-[#2C3138] hover:text-black">
            <Scale size={10} /> File
          </Link>
        ) : (
          <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-[#8A9098]">
            Reference
          </span>
        )}
        <div className="flex items-center gap-3">
          <a href={url} target="_blank" rel="noreferrer"
             data-testid={`bureaucratic-preview-${t.slug}`}
             className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.22em] text-[#2C3138] hover:text-black">
            <ExternalLink size={10} /> Preview
          </a>
          <a href={url} download
             className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.22em] text-[#2C3138] hover:text-black">
            <Download size={10} /> PDF
          </a>
        </div>
        </div>
      </div>
    </div>
  );
}
